import { useState } from "react";

type HistoricalExplorerProps = {
  candidateReturn: number;
  spyReturn: number;
  startDate: string;
  endDate: string;
  oneWayCostBps: number;
};

const pct = (value: number, digits = 2) => `${(value * 100).toFixed(digits)}%`;
const signedPct = (value: number, digits = 2) => `${value >= 0 ? "+" : ""}${pct(value, digits)}`;

export function HistoricalExplorer({
  candidateReturn,
  spyReturn,
  startDate,
  endDate,
  oneWayCostBps,
}: HistoricalExplorerProps) {
  const [view, setView] = useState<"result" | "decision">("result");
  const scale = Math.max(candidateReturn, spyReturn);
  const candidateWidth = `${Math.max(0, (candidateReturn / scale) * 100)}%`;
  const spyWidth = `${Math.max(0, (spyReturn / scale) * 100)}%`;
  const startingWealth = 10_000;
  const candidateEndingWealth = startingWealth * (1 + candidateReturn);
  const spyEndingWealth = startingWealth * (1 + spyReturn);
  const endingWealthAdvantage = candidateEndingWealth - spyEndingWealth;
  const returnAdvantage = candidateReturn - spyReturn;
  const relativeEndingWealthAdvantage = endingWealthAdvantage / spyEndingWealth;
  const oneWayCostPercent = oneWayCostBps / 100;
  const dollars = (value: number) => value.toLocaleString("en-US", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    style: "currency",
    currency: "USD",
  });

  return (
    <section className="range-explorer" aria-labelledby="historical-explorer-title">
      <div className="range-explorer-heading">
        <div>
          <p className="kicker">Historical replay</p>
          <h3 id="historical-explorer-title">In this replay, Finly ended with 56.7% more money than SPY.</h3>
        </div>
        <p>
          Both started with $10,000 and used the same 2013–2026 prices. Finly returned +967.11% versus
          +580.82% for SPY, a fund that tracks the S&amp;P 500.
        </p>
      </div>

      <div className="range-tabs" role="group" aria-label="Choose a view of the historical comparison">
        <button
          type="button"
          aria-pressed={view === "result"}
          aria-controls="historical-result-panel"
          onClick={() => setView("result")}
        >
          Ending wealth
        </button>
        <button
          type="button"
          aria-pressed={view === "decision"}
          aria-controls="historical-decision-panel"
          onClick={() => setView("decision")}
        >
          How to read this
        </button>
      </div>

      {view === "result" ? (
        <div
          className="audit-panel"
          id="historical-result-panel"
          aria-live="polite"
        >
          <div
            className="audit-comparison"
            role="img"
            aria-label={`In the historical replay, Finly returned ${pct(candidateReturn)} and SPY returned ${pct(spyReturn)} from ${startDate} through ${endDate}.`}
          >
            <div className="audit-bar-row">
              <div className="audit-bar-label">
                <span>Finly</span>
                <strong>{signedPct(candidateReturn)}</strong>
              </div>
              <div className="audit-bar-track" aria-hidden="true">
                <i className="audit-bar-g4" style={{ width: candidateWidth }} />
              </div>
            </div>
            <div className="audit-bar-row">
              <div className="audit-bar-label">
                <span>SPY · S&amp;P 500 tracker</span>
                <strong>{signedPct(spyReturn)}</strong>
              </div>
              <div className="audit-bar-track" aria-hidden="true">
                <i className="audit-bar-spy" style={{ width: spyWidth }} />
              </div>
            </div>
          </div>
          <p className="audit-panel-conclusion">
            A simulated {dollars(startingWealth)} became approximately <strong>{dollars(candidateEndingWealth)}</strong>
            {" "}with Finly versus <strong>{dollars(spyEndingWealth)}</strong> with SPY—a historical ending-wealth difference of
            {" "}<strong>{dollars(endingWealthAdvantage)}</strong>. The replay covers {startDate} through {endDate} and
            includes a modeled {oneWayCostPercent.toFixed(2)}% cost whenever the portfolio trades.
          </p>
        </div>
      ) : (
        <div
          className="audit-panel audit-panel-decision"
          id="historical-decision-panel"
          aria-live="polite"
        >
          <dl className="audit-decision-ledger">
            <div>
              <dt>Historical ending-wealth difference</dt>
              <dd>{dollars(endingWealthAdvantage)}</dd>
              <p>{pct(relativeEndingWealthAdvantage)} more wealth than SPY from the same simulated $10,000.</p>
            </div>
            <div>
              <dt>Historical growth gap</dt>
              <dd>{signedPct(returnAdvantage)}</dd>
              <p>A 386.29-percentage-point difference across the same historical window.</p>
            </div>
            <div className="audit-disposition">
              <dt>Cost discipline</dt>
              <dd>{oneWayCostPercent.toFixed(2)}%</dd>
              <p>The replay subtracts this modeled cost each time the portfolio trades.</p>
            </div>
          </dl>
          <p className="audit-panel-conclusion">
            We chose this portfolio for the paper competition and locked it before the test began. The historical replay
            explains that choice; the live paper account is measured separately.
          </p>
        </div>
      )}

      <p className="range-boundary">
        <strong>How this was measured:</strong> past prices from {startDate} to {endDate}; the same $10,000 starting amount
        and dates for both portfolios; a modeled {oneWayCostPercent.toFixed(2)}% trading cost. The separate $100,000 paper
        account appears in the live section above.
      </p>
    </section>
  );
}
